// Benutzer-Menü unten in der Sidebar (design-system §3.8): Darstellung direkt umschaltbar, Tastenkürzel, „Ordaneo
// aktualisieren“ (neueste Fassung holen, ohne Abmelden – statt Websitedaten löschen), Abmelden.
import { brand } from '@shared/brand'
import { Keyboard, LogOut, RefreshCw, UserRound } from 'lucide-react'
import { useNavigate } from 'react-router'
import { useSession } from '../api/session'
import { cn } from '../lib/cn'
import { useTheme, type ThemeChoice } from '../lib/theme'
import { Avatar } from '../ui/Avatar'
import { DropdownMenu, type MenuEntry } from '../ui/DropdownMenu'
import { updateNow } from './pwa'
import { signOut } from './signOut'

const THEMES: { value: ThemeChoice; label: string }[] = [
  { value: 'light', label: 'Hell' },
  { value: 'dark', label: 'Dunkel' },
  { value: 'system', label: 'Wie das System' },
]

export interface UserMenuProps {
  /** 'row': Avatar und Name (Sidebar) · 'avatar': nur der Avatar (Rail, Kopfzeile auf dem Telefon) */
  variant?: 'row' | 'avatar'
  onShowShortcuts?: () => void
  side?: 'top' | 'bottom' | 'right'
  className?: string
}

export function UserMenu({ variant = 'row', onShowShortcuts, side = 'top', className }: UserMenuProps) {
  const session = useSession()
  const theme = useTheme()
  const navigate = useNavigate()
  if (!session) return null
  const { user } = session
  const location = session.locations.find((entry) => entry.id === session.activeLocationId)

  const items: MenuEntry[] = [
    { type: 'label', id: 'theme', label: 'Darstellung' },
    ...THEMES.map((entry): MenuEntry => ({
      type: 'radio',
      id: `theme-${entry.value}`,
      label: entry.label,
      checked: theme.choice === entry.value,
      onSelect: () => theme.setTheme(entry.value),
    })),
    { type: 'separator', id: 'sep-actions' },
    // eigenes Profil (Name, PIN für Freigaben, Sitzungen) – die Seite liefert das Modul platform
    { id: 'profile', label: 'Mein Profil', icon: UserRound, onSelect: () => void navigate('/settings/profile') },
    ...(onShowShortcuts
      ? [
          {
            id: 'shortcuts',
            label: 'Tastenkürzel',
            icon: Keyboard,
            hotkey: '?',
            onSelect: onShowShortcuts,
          } as MenuEntry,
        ]
      : []),
    // neueste Fassung sicher holen (Service Worker und Hülle neu) – mit Neuladen-Sperre oder ohne Verbindung nur ein Hinweis
    { id: 'update', label: `${brand.name} aktualisieren`, icon: RefreshCw, onSelect: () => void updateNow() },
    {
      id: 'logout',
      label: 'Abmelden',
      icon: LogOut,
      // ein Abmelde-Wächter (Kasse: Bons warten) sperrt mit einem Hinweis statt abzumelden
      onSelect: async () => {
        await signOut(() => void navigate('/login', { replace: true }))
      },
    },
  ]

  return (
    <DropdownMenu
      side={side}
      align="start"
      width="sm"
      items={items}
      footer={
        // wer hier arbeitet und wo – als ruhiger Text, nicht als Menüpunkt
        <div
          className="-mx-1 mt-1 border-t border-line px-3 pt-2 pb-1.5 text-caption text-fg-subtle"
          data-testid="user-menu-account"
        >
          <p className="truncate-line text-fg-muted">{user.email}</p>
          <p className="truncate-line">{location ? `${location.code} ${location.name}` : 'Alle Standorte'}</p>
        </div>
      }
      trigger={
        <button
          type="button"
          data-testid="user-menu"
          aria-label={`Benutzer-Menü: ${user.displayName}`}
          className={cn(
            'flex min-w-0 items-center gap-2 rounded-sm text-left text-dense text-fg transition-colors duration-(--duration-instant) hover:bg-nav-hover touch:text-touch',
            variant === 'row' ? 'h-9 w-full px-2 touch:h-11' : 'size-9 justify-center touch:size-11',
            className,
          )}
        >
          <Avatar name={user.displayName} size="sm" decorative />
          {variant === 'row' && <span className="min-w-0 flex-1 truncate-line">{user.displayName}</span>}
        </button>
      }
    />
  )
}
